import { PDFDocument } from 'pdf-lib';
import { useMemo,useEffect,useRef } from 'react';
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import jsPDF from 'jspdf';
import 'jspdf-autotable'; // Import the jsPDF autoTable plugin
import PropTypes from 'prop-types'; 
import { addDecaQrToShippingPdf } from 'pages/shipping/deca/addDecaQrToShippingPdf';

const buildMenuPermissions = (roles) => {
  const list = roles?.[0]?.listmenus ?? [];
  const has = (name) => list.some((m) => m.name === name);
  return {
    canViewPrice: has('View_Prices_Shipping') ,
    canViewExpandedDestination: roles?.some(role => role.listmenus?.some(menu => menu.name === 'SubMenu_Shipping_EXPANDED_DESTINATION')) ?? false,
    canViewOptTras: has('View_OptTras_Shipping'),
    canViewInspectorSignature: roles?.some(role => role.listmenus?.some(menu => menu.name === 'SubMenu_Shipping_VIEW_INSPECTOR_SIGNATURE')) ?? false
  };
}; 

const ReportTemplateShipping = ({
  dataShipping,
  initialStateShipping,
  setDataShipping,
  setViewPDF,
  setPdfBlob,
}) => {
  const { t, i18n } = useTranslation(['listDataShipping']);
  const roles = useSelector((state) => state.loginUser.roles);  
  const token = useSelector((state) => state.loginUser.token);
  const headerPrinterShipping = useSelector((state) => state.formatDIUser.headerPrinter);
  const menuPermissions = useMemo(
     () => buildMenuPermissions(roles),
     [roles]
   );
  const generatedPdfKeyRef = useRef(null);

  // Función para formatear el número
  function formatNumber(num) {
    return num.toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
  }

  function formatNumberEuro(num) {
    return num.toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }) + ' €';
  }
  
  function formatTax(num) {
    return num.toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }) + ' %';
  }

  function getDateFormated(getDate) {
    //let newDate=(getDate !==null && getDate !=="")? new Date(getDate).toLocaleDateString(i18n.language === "es" ? "es-ES" : "en-US"): new Date().toLocaleDateString(i18n.language === "es" ? "es-ES" : "en-US");
    const meses = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
    const hoy = (getDate !==null && getDate !=="")? new Date(getDate):new Date();
    const dia = hoy.getDate();
    const mes = meses[hoy.getMonth()];
    const año = hoy.getFullYear();
    return `${dia} / ${mes} / ${año}`;
  }
  const isValidPngDataURL = (value) => {
    if (typeof value !== "string") return false;
    const trimmed = value.trim();
    if (trimmed==="") return false;
    if (trimmed.includes("static/media")) return true; 
    if (!trimmed.startsWith("data:image/png;base64,")) return false;
    // longitud mínima razonable para un PNG (ajusta si quieres)
    return trimmed.length > 200; 
  }; 
  function groupTaxes(dataShipping) {
    const taxGroups = [];
    dataShipping.linesshipping.forEach((line) => {
      const { tax, amount, estimatedprice, discount, taxincluded } = line;
      const taxNeto = (amount * estimatedprice * (1 - discount / 100));
      if (tax !== 0 && !taxincluded && taxNeto !== 0) {
        const taxAmount = (taxNeto * (tax / 100));
        const existingTaxGroup = taxGroups.find(
          (group) => group.taxRate === tax
        );
        if (existingTaxGroup) {
          existingTaxGroup.taxAmount += taxAmount;
        } else {
          taxGroups.push({ taxRate: tax, taxAmount });
        }
      }
    });
    return taxGroups;
  }
  useEffect(() => {
    const generatePDF = async () => {
      // Importa jsPDF
      const doc = new jsPDF({
        orientation: 'p', // 'p' para vertical, 'l' para horizontal
        unit: 'mm', // Unidad en milímetros
        format: [210, menuPermissions.canViewExpandedDestination ? Math.max(420, 420 + dataShipping.linesshipping.length * 30) : 297] // Ancho y alto en milímetros
      });  
      const taxGroups = groupTaxes(dataShipping);
      let counter=0;
      let yOffset = 10; // Margen superior para comenzar a imprimir
      if (headerPrinterShipping && headerPrinterShipping.length > 0) {
        // Dividimos en líneas
        headerPrinterShipping.split('\n').forEach((line) => {
          if (counter===0){
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
          }else{
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
          }
          counter++;
          doc.text(line, 40, yOffset, 'center');
          yOffset += 3;
        });
      }  
      yOffset = 16;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('ALBARÁN/CARTA DE PORTES N.º', 80, yOffset-5, 'left');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text('Orden FOM/2861/2012BOE nº 5 de 5 de enero de 2013', 80, yOffset, 'left');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      const formatted_id = dataShipping.ownshipping_id.toString().padStart(6, '0');
      doc.text(`${formatted_id}`, 190, yOffset-5, 'right');
      yOffset = 34;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('CAMIÓN/Matrícula: ', 80, yOffset-11, 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${String(dataShipping.vehiclestore_licenseplate ?? '').substr(0, 18)}`, 114, yOffset-12, 'left');
      doc.setFont('helvetica', 'bold');
      doc.text('Fecha: ', 160, yOffset-11, 'left');
      doc.setFont('helvetica', 'normal');
      const newDate = (dataShipping.date_shipping!==undefined && dataShipping.date_shipping!==null && dataShipping.date_shipping!=="")?getDateFormated(dataShipping.date_shipping):"";
      doc.text(`${newDate}`, 200, yOffset-12, 'right');
      doc.setFont('helvetica', 'bold');
      doc.text('REMOLQUE/TIPO: ', 80, yOffset-4, 'left');
      doc.setFont('helvetica', 'normal');
      let trailer = dataShipping.vehiclestore_trailer.trim();
      if (dataShipping.semitrailertype_namees!==null && dataShipping.semitrailertype_namees!=="") {
        trailer=trailer+" / "+dataShipping.semitrailertype_namees.trim()
      }
      doc.text(`${trailer.substr(0, 20)}`,113, yOffset-5, 'left');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('MODELO GRÚA: ', 160, yOffset-4, 'left');
      doc.setFont('helvetica', 'normal');
      
      doc.text(`${dataShipping.cranemodel_namees.substr(0, 14)}`,200, yOffset-5, 'right');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Destinatario/Cliente: ', 5, yOffset+5, 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${dataShipping.dest_tras_tradename.substr(0, 52)}`, 40, yOffset+4, 'left');
      doc.setFont('helvetica', 'bold');
      doc.text('CIF: ', 160, yOffset+5, 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${dataShipping.dest_tras_documentid.substr(0, 15)}`, 170, yOffset+4, 'left');
      doc.setFont('helvetica', 'bold');
      doc.text('Domicilio: ', 5, yOffset+10, 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${dataShipping.dest_tras_street.substr(0, 110)}`, 23, yOffset+9, 'left');
      const showOptTras = menuPermissions.canViewOptTras;
      const optTrasYOffset = showOptTras ? 0 : -10;
      const detailY = (position) => yOffset + position + optTrasYOffset;
      if (showOptTras) {
        doc.setFont('helvetica', 'bold');
        doc.text('Empresa Operadora: ', 5, yOffset+15, 'left');
        doc.setFont('helvetica', 'normal');
        doc.text(`${dataShipping.opt_tras_tradename.substr(0, 52)}`, 40, yOffset+14, 'left');
        doc.setFont('helvetica', 'bold');
        doc.text('CIF: ', 160, yOffset+15, 'left');
        doc.setFont('helvetica', 'normal');
        doc.text(`${dataShipping.opt_tras_documentid.substr(0, 15)}`, 170, yOffset+14, 'left');
        doc.setFont('helvetica', 'bold');
        doc.text('Domicilio: ', 5, yOffset+20, 'left');
        doc.setFont('helvetica', 'normal');
        doc.text(`${dataShipping.opt_tras_street.substr(0, 110)}`, 23, yOffset+19, 'left');
      }
      doc.setFont('helvetica', 'bold');
      doc.text('Transportista: ', 5, detailY(25), 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${dataShipping.rel_trans_tradename.substr(0, 58)}`, 30, detailY(24), 'left');
      doc.setFont('helvetica', 'bold');
      doc.text('D.N.I.: ', 160, detailY(25), 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${dataShipping.rel_trans_documentid.substr(0, 19)}`, 173, detailY(24), 'left');
      doc.setFont('helvetica', 'bold');
      doc.text('Origen Carga: ', 5, detailY(30), 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${dataShipping.rel_trans_street.substr(0, 105)}`, 30, detailY(29), 'left');
      doc.setFont('helvetica', 'bold');
      doc.text('Conductor: ', 5, detailY(35), 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${dataShipping.vehiclestore_defaultdriver.substr(0, 62)}`, 25, detailY(34), 'left');
      doc.setFont('helvetica', 'bold');
      doc.text('D.N.I.: ', 160, detailY(35), 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${dataShipping.vehiclestore_documentid.substr(0, 15)}`, 173, detailY(34), 'left')
      let routeExtraHeight = 0;
      let expandedOriginLines = [];
      let expandedDestinationLines = [];
      if (menuPermissions.canViewExpandedDestination) {
        doc.setFont('helvetica', 'normal');
        expandedOriginLines = doc.splitTextToSize(String(dataShipping.origin_street || ''), 170);
        expandedDestinationLines = doc.splitTextToSize(String(dataShipping.destine_street || ''), 170).slice(0, 30);
        const originHeight = Math.max(1, expandedOriginLines.length) * 5;
        const destinationHeight = Math.max(1, expandedDestinationLines.length) * 5;
        doc.setFont('helvetica', 'bold');
        doc.text('Lugar Origen : ', 5, detailY(40), 'left');
        doc.setFont('helvetica', 'normal');
        expandedOriginLines.forEach((line, index) => doc.text(line, 30, detailY(40) + index * 5));
        doc.setFont('helvetica', 'bold');
        doc.text('Lugar Destino : ', 5, detailY(40) + originHeight, 'left');
        doc.setFont('helvetica', 'normal');
        expandedDestinationLines.forEach((line, index) => doc.text(line, 30, detailY(40) + originHeight + index * 5));
        routeExtraHeight = originHeight + destinationHeight - 5;
      } else {
      doc.setFont('helvetica', 'bold');
      doc.text('Lugar Origen : ', 5, detailY(40), 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${dataShipping.origin_street.substr(0, 30)}`, 30, detailY(40), 'left');
      doc.setFont('helvetica', 'bold');
      doc.text('Lugar Destino : ', 98, detailY(40), 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${dataShipping.destine_street.substr(0, 32)}`, 125, detailY(40), 'left');
      }
      doc.setFont('helvetica', 'bold');
      doc.text('Obra : ', 5, detailY(45) + routeExtraHeight, 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${dataShipping.workplace.substr(0, 86)}`, 16, detailY(45) + routeExtraHeight, 'left');
      doc.setFont('helvetica', 'bold');
      doc.text('Observaciones: ', 5, detailY(50) + routeExtraHeight, 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${String(dataShipping.observations ?? '').substr(0, 82)}`, 33, detailY(50) + routeExtraHeight, 'left');
      const shippingHeaderYOffset = yOffset;
      yOffset = detailY(5) + routeExtraHeight;

      const shippingContentYOffset = yOffset;
      yOffset = shippingHeaderYOffset;
      doc.setLineDash([0.2, 0.2], 0); // [2, 2] define el patrón de los puntos: 2px línea, 2px espacio
      doc.setFillColor('#0d0b0b');
      doc.setLineWidth(0.3);  
      doc.line(114, yOffset-11, 155, yOffset-11);  
      doc.line(175, yOffset-11, 200, yOffset-11); 
      doc.line(113, yOffset-4, 155, yOffset-4);  
      doc.line(181, yOffset-4, 200, yOffset-4);  
      doc.line(40, yOffset+5, 155, yOffset+5);  
      doc.line(169, yOffset+5, 200, yOffset+5);  
      doc.line(23, yOffset+10, 200, yOffset+10); 
      if (showOptTras) {
        doc.line(40, yOffset+15, 155, yOffset+15);  
        doc.line(169, yOffset+15, 200, yOffset+15);  
        doc.line(23, yOffset+20, 200, yOffset+20);  
      }
      doc.line(30, detailY(25), 155, detailY(25));  
      doc.line(172, detailY(25), 200, detailY(25));  
      doc.line(30, detailY(30), 200, detailY(30));  
      doc.line(25, detailY(35), 155, detailY(35)); 
      doc.line(172, detailY(35), 200, detailY(35));  
      if (menuPermissions.canViewExpandedDestination) {
        expandedOriginLines.forEach((_, index) => doc.line(30, detailY(41) + index * 5, 200, detailY(41) + index * 5));
        const originHeight = Math.max(1, expandedOriginLines.length) * 5;
        expandedDestinationLines.forEach((_, index) => doc.line(30, detailY(41) + originHeight + index * 5, 200, detailY(41) + originHeight + index * 5));
      } else {
        doc.line(30, detailY(41), 97, detailY(41));
        doc.line(125, detailY(41), 200, detailY(41));
      }
      doc.line(16, detailY(46) + routeExtraHeight, 200, detailY(46) + routeExtraHeight);
      doc.line(31, detailY(51) + routeExtraHeight, 200, detailY(51) + routeExtraHeight);
      yOffset = shippingContentYOffset;
      doc.setFont('helvetica', 'bold');
      doc.text('Trabajo efectuado por : ', 5, yOffset+53, 'left');
    
      
      doc.setFont('helvetica', 'bold');
      doc.text('Horas de:', 45, yOffset+53, 'left');

      let Hour = dataShipping.hours
      ? new Date(dataShipping.hours).toLocaleTimeString(
          i18n.language === "es" ? "es-ES" : "en-US",
          {
            hour: '2-digit',
            minute: '2-digit'
          }
        )
      : '';
      doc.setFont('helvetica', 'normal'); 
      doc.text(Hour, 64, yOffset+53, 'left');

      doc.setFont('helvetica', 'normal'); 
      doc.rect(78, yOffset + 49, 4, 4); // x, y, width, height
      doc.setFont('helvetica', 'bold');
      doc.text('Con Grúa', 83, yOffset+53, 'left');
      if (dataShipping.withcrane) {
        doc.setFontSize(10);
        doc.text('X', 79, yOffset + 52.5);
      }
      doc.setFont('helvetica', 'normal'); 
      doc.rect(101, yOffset + 49, 4, 4); // x, y, width, height
      doc.setFont('helvetica', 'bold');
      doc.text('Sin Grúa', 106, yOffset+53, 'left');
      if (dataShipping.withoutcrane) {
        doc.setFontSize(10);
        doc.text('X', 102, yOffset + 52.5);
      }
      doc.setFont('helvetica', 'normal'); 
      doc.rect(123, yOffset + 49, 4, 4); // x, y, width, height
      doc.setFont('helvetica', 'bold');
      doc.text('ADR', 128, yOffset+53, 'left');
      if (dataShipping.adr) {
        doc.setFontSize(10);
        doc.text('X', 124, yOffset + 52.5);
      }
      doc.setFont('helvetica', 'normal'); 
      doc.rect(138, yOffset + 49, 4, 4); // x, y, width, height
      doc.setFont('helvetica', 'bold');
      doc.text('Otros', 143, yOffset+53, 'left');
      if (dataShipping.others) {
        doc.setFontSize(10);
        doc.text('X', 139, yOffset + 52.5);
      }
      yOffset+=5;

      doc.setLineDash([]);
      doc.setFillColor('#FFFFFF');
      const importsYOffset = 0;
      const articleDescriptionLines = dataShipping.linesshipping.map((line) =>
        doc.splitTextToSize(line.article_namees || '', 125)
      );
      const articleExtraHeights = articleDescriptionLines.map((lines) =>
        Math.max(0, lines.length - 1) * 5
      );
      // Deja margen bajo el importe de la última línea para que la separación
      // del resumen fiscal no atraviese el texto.
      const articleRowsHeight = Math.max(
        15,
        4 + articleExtraHeights.reduce((height, extraHeight) => height + 12 + extraHeight, 0)
      );
      const drawDoubleRect = (x, y, w, h, gap = 0.8) => {
        doc.setLineWidth(0.4);
        doc.rect(x, y, w, h);
        doc.setLineWidth(0.2);
        doc.rect(x + gap, y + gap, w - gap * 2, h - gap * 2);
      };
      const taxSummaryHeight = 12 + (5 * taxGroups.length);
      // --- Tus rectángulos con doble línea ---
      drawDoubleRect(155, yOffset + 43 + importsYOffset, 45, 7);
      drawDoubleRect(155, yOffset + 50 + importsYOffset, 45, articleRowsHeight);
      drawDoubleRect(155, yOffset + 50 + importsYOffset + articleRowsHeight, 45, taxSummaryHeight);
      
      yOffset+=13;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('IMPORTES', 168, yOffset+35+importsYOffset, 'left');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Servicios: ', 5, yOffset+40, 'left');
      doc.setFont('helvetica', 'normal');
      doc.text(`${dataShipping.service_1.substr(0, 62)}`, 25, yOffset+39, 'left');
      doc.setFont('helvetica', 'bold');
      doc.text('Peso: ', 110, yOffset+40, 'left');
      doc.setFont('helvetica', 'normal');
      if (dataShipping.weight!==0){
        doc.text(`${formatNumber(dataShipping.weight)} kg`, 150, yOffset+39, 'right');
      }
      let totalShipping=0;
      doc.line(23, yOffset+40, 105, yOffset+40);
      doc.line(120, yOffset+40, 150, yOffset+40);
      yOffset=yOffset+41+importsYOffset;
      if (dataShipping.linesshipping && dataShipping.linesshipping.length>0){
        for (let i = 0; i < dataShipping.linesshipping.length; i++) {
          const descriptionLines = articleDescriptionLines[i];
          const articleExtraHeight = articleExtraHeights[i];
          doc.setFont('helvetica', 'bold');
          doc.text('Descripción: ', 5, yOffset+5, 'left');
          doc.setFont('helvetica', 'normal');
          for (let lineIndex = 0; lineIndex < descriptionLines.length; lineIndex++) {
            doc.text(descriptionLines[lineIndex], 27, yOffset + 4 + (lineIndex * 5), 'left');
            doc.line(27, yOffset + 5 + (lineIndex * 5), 155, yOffset + 5 + (lineIndex * 5));
          }
          doc.setFont('helvetica', 'bold');
          doc.text('Cant.: ', 14, yOffset+10+articleExtraHeight, 'left');
          doc.setFont('helvetica', 'normal');
          doc.text(`${formatNumber(dataShipping.linesshipping[i].amount)}`, 45, yOffset+9+articleExtraHeight, 'right');
          doc.setFont('helvetica', 'bold');
          doc.text('Precio: ', 53, yOffset+10+articleExtraHeight, 'left');
          doc.setFont('helvetica', 'normal');
          let price=dataShipping.linesshipping[i].estimatedprice*(1-(dataShipping.linesshipping[i].discount/100));
          if (menuPermissions.canViewPrice){
            doc.text(`${formatNumberEuro(price)}`, 88, yOffset+9+articleExtraHeight, 'right');
          } 
          doc.setFont('helvetica', 'bold');
          doc.text('I.G.I.C.: ', 93, yOffset+10+articleExtraHeight, 'left');
          doc.setFont('helvetica', 'normal');
          if (menuPermissions.canViewPrice){
            doc.text(`${formatTax(dataShipping.linesshipping[i].tax)}`, 123, yOffset+9+articleExtraHeight, 'right');
          } 
          doc.setFont('helvetica', 'bold');
          doc.text('Incluido: ', 128, yOffset+10+articleExtraHeight, 'left');
          // Dibujar el cuadrado
          doc.rect(146, yOffset + 6 + articleExtraHeight, 4, 4); // x, y, width, height
          // Si está marcado → dibujar check
          doc.setFont('helvetica', 'normal');
          if (menuPermissions.canViewPrice){
            if (dataShipping.linesshipping[i].taxincluded) {
              doc.setFontSize(10);
              doc.text('X', 147, yOffset + 9.5 + articleExtraHeight);
            }
          }
          let totalNetoWeight=(dataShipping.linesshipping[i].amount*price);
          if (!dataShipping.linesshipping[i].taxincluded){
            totalNetoWeight=(totalNetoWeight)*(1+(dataShipping.linesshipping[i].tax/100));
          }
          totalShipping+=totalNetoWeight;
          if (menuPermissions.canViewPrice){
            doc.text(`${formatNumberEuro(totalNetoWeight)}`, 196, yOffset+9+articleExtraHeight, 'right');
          }
          doc.line(27, yOffset+10+articleExtraHeight, 50, yOffset+10+articleExtraHeight); // Dibuja una línea punteada desde (10, 20) hasta (200, 20)
          doc.line(67, yOffset+10+articleExtraHeight, 90, yOffset+10+articleExtraHeight); // Dibuja una línea punteada desde (10, 20) hasta (200, 20)
          doc.line(108, yOffset+10+articleExtraHeight, 125, yOffset+10+articleExtraHeight); // Dibuja una línea punteada desde (10, 20) hasta (200, 20)
          doc.line(158, yOffset+10+articleExtraHeight, 197, yOffset+10+articleExtraHeight); // Dibuja una línea punteada desde (10, 20) hasta (200, 20      
          yOffset=yOffset+11+articleExtraHeight;
        }
      }
      if (menuPermissions.canViewPrice){
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text("FORMA DE PAGO: ", 70, yOffset+8, 'right');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(`${dataShipping.paymenttype_namees}`, 70, yOffset+8, 'left');
      }
      doc.setFontSize(9);
      doc.text('Recibí Conforme: ', 5, yOffset+15, 'left');
      doc.text('Encargado Obra: ', 5, yOffset+18, 'left');
      doc.text('El Chófer: ', 55, yOffset+18, 'left');
      doc.text('El Cargador: ', 93, yOffset+18, 'left');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.line(30, yOffset+19, 50, yOffset+19);
      doc.line(70, yOffset+19, 90, yOffset+19);
      doc.line(112, yOffset+19, 132, yOffset+19);
      if (menuPermissions.canViewInspectorSignature && isValidPngDataURL(dataShipping.signature_inspector)) {
        try {
          doc.addImage(dataShipping.signature_inspector, 'PNG', 10, yOffset+20, 30, 20);
        } catch (e) {
          console.warn('No se pudo mostrar la firma del encargado de obra en el PDF.');
        }
      }
      if (dataShipping.signature_driver!==undefined && dataShipping.signature_driver!=="" && isValidPngDataURL(dataShipping.signature_driver)){
        try {
            doc.addImage(`${dataShipping.signature_driver}`, 'PNG', 60, yOffset+20, 30, 20);
          } catch (e) { 
        }
      }  
      if (dataShipping.signature_charger!==undefined && dataShipping.signature_charger!=="" && isValidPngDataURL(dataShipping.signature_charger)){
        try {
              doc.addImage(`${dataShipping.signature_charger}`, 'PNG', 100, yOffset+20, 30, 20);
          } catch (e) { 
        }
      }
      yOffset=yOffset+6+dataShipping.linesshipping.length;
      if (taxGroups && taxGroups.length > 0) {
        taxGroups.forEach((group) => {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.text(`IGIC ${formatTax(group.taxRate)} :`, 178, yOffset, 'right');
          if (menuPermissions.canViewPrice){
            doc.text(`${formatNumberEuro(group.taxAmount)}`, 196, yOffset, 'right');
          }
          doc.line(179, yOffset+1, 196, yOffset+1);  
          yOffset += 4;
        });
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text("TOTAL", 152, yOffset+4, 'right');
      doc.setFontSize(12);
      if (menuPermissions.canViewPrice){
        doc.text(`${formatNumberEuro(totalShipping)}`, 196, yOffset+4, 'right');
      }
      doc.line(158, yOffset+5, 197, yOffset+5); // Dibuja una línea punteada desde (10, 20) hasta (200, 20      
      const decaQrY = yOffset + 14;
      await addDecaQrToShippingPdf(
        doc,
        dataShipping.id,
        token,
        { x: 167.5, y: decaQrY, size: 20 }
      );
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const footerYWithoutQr = taxGroups.length > 1
        ? yOffset + 30 - taxGroups.length
        : yOffset + 36;
      // Mantiene el aviso siempre por debajo del QR, incluso cuando crece
      // dinámicamente la zona de artículos e impuestos.
      yOffset = Math.max(footerYWithoutQr, decaQrY + 26);
      doc.text('El transportista no se hace responsable, en ningún momento de los daños que pudieran ocasionar a la mercancía, así como los daños y perjuicios causados', 5, yOffset, 'left');
      doc.text('a tuberías, cables y otros similares que no sean visibles.', 5, yOffset+4, 'left');
    let pdfBlob = doc.output('blob');
    if (menuPermissions.canViewExpandedDestination) {
      // Keep all sections together on one A4 sheet; fit the complete content.
      const sourcePdf = await PDFDocument.load(doc.output('arraybuffer'));
      const sourcePage = sourcePdf.getPages()[0];
      const mmToPoints = 72 / 25.4;
      const contentHeight = Math.max(297, yOffset + 14) * mmToPoints;
      const sourceHeight = sourcePage.getHeight();
      const finalPdf = await PDFDocument.create();
      const embedded = await finalPdf.embedPage(sourcePage, {
        left: 0, bottom: sourceHeight - contentHeight,
        right: 210 * mmToPoints, top: sourceHeight,
      });
      const page = finalPdf.addPage([210 * mmToPoints, 297 * mmToPoints]);
      const scale = Math.min(1, page.getHeight() / contentHeight);
      page.drawPage(embedded, {
        x: (page.getWidth() - embedded.width * scale) / 2,
        y: page.getHeight() - embedded.height * scale,
        width: embedded.width * scale, height: embedded.height * scale,
      });
      pdfBlob = new Blob([await finalPdf.save()], { type: 'application/pdf' });
    }
    if (setPdfBlob) {
      setPdfBlob(pdfBlob);
    } else {
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
    }
    if (setViewPDF) setViewPDF(false);
    if (setDataShipping && initialStateShipping) {
      setDataShipping(initialStateShipping);
    }
  };

  if (!dataShipping?.id) return;
  const generationKey = [
    dataShipping.id,
    i18n.language,
    menuPermissions.canViewPrice,
    menuPermissions.canViewOptTras,
    menuPermissions.canViewExpandedDestination,
    menuPermissions.canViewInspectorSignature,
    headerPrinterShipping,
  ].join(':');
  if (generatedPdfKeyRef.current === generationKey) return;
  generatedPdfKeyRef.current = generationKey;
  generatePDF();
}, [dataShipping, t,i18n,setViewPDF,setPdfBlob,setDataShipping,initialStateShipping,headerPrinterShipping,menuPermissions.canViewPrice,menuPermissions.canViewOptTras,menuPermissions.canViewInspectorSignature,menuPermissions.canViewExpandedDestination,token]);
  return null; // This component does not render anything in the DOM
};

ReportTemplateShipping.propTypes = {
  dataShipping: PropTypes.object.isRequired, 
  initialStateShipping: PropTypes.object,
  setDataShipping: PropTypes.func,
  setViewPDF: PropTypes.func,
  setPdfBlob: PropTypes.func,
};

export default ReportTemplateShipping;
